/**
 * DatePickerDropdown — Reusable styled date picker dropdown using react-day-picker.
 * Matches the app's dark/light theme with Tailwind classes.
 */
import React, { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const dayPickerClassNames = {
  months: 'flex flex-col',
  month: 'space-y-2',
  caption: 'flex justify-center relative items-center h-8',
  caption_label: 'text-sm font-medium text-gray-900 dark:text-white',
  nav: 'flex items-center',
  nav_button: 'p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300',
  nav_button_previous: 'absolute left-0',
  nav_button_next: 'absolute right-0',
  table: 'w-full border-collapse',
  head_row: 'flex',
  head_cell: 'w-8 text-[10px] font-medium text-gray-500 dark:text-gray-400 text-center',
  row: 'flex w-full mt-0.5',
  cell: 'w-8 h-8 text-center text-sm relative',
  day: 'w-8 h-8 rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/40 text-gray-900 dark:text-gray-100 transition-colors text-sm',
  day_selected: 'bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-500 font-semibold',
  day_today: 'font-bold text-indigo-600 dark:text-indigo-400',
  day_disabled: 'text-gray-300 dark:text-gray-600 hover:bg-transparent cursor-not-allowed',
  day_outside: 'text-gray-300 dark:text-gray-600',
};

const dayPickerComponents = {
  IconLeft: () => <ChevronLeft className="w-4 h-4" />,
  IconRight: () => <ChevronRight className="w-4 h-4" />,
};

interface DatePickerDropdownProps {
  /** Currently selected date (YYYY-MM-DD) or null */
  selected: string | null;
  /** Called with YYYY-MM-DD when user picks a date */
  onSelect: (date: string) => void;
  /** The trigger element — receives onClick to toggle the dropdown */
  children: (props: { onClick: () => void; ref: React.RefObject<HTMLDivElement> }) => React.ReactNode;
  /** Optional min date constraint (YYYY-MM-DD) */
  min?: string;
  /** Optional max date constraint (YYYY-MM-DD) */
  max?: string;
  /** Alignment of dropdown — default 'left' */
  align?: 'left' | 'right';
}

const DatePickerDropdown: React.FC<DatePickerDropdownProps> = ({
  selected,
  onSelect,
  children,
  min,
  max,
  align = 'left',
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null!);

  const selectedDate = selected ? new Date(selected + 'T00:00:00') : undefined;
  const fromDate = min ? new Date(min + 'T00:00:00') : undefined;
  const toDate = max ? new Date(max + 'T00:00:00') : undefined;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block">
      {children({ onClick: () => setOpen(o => !o), ref: containerRef })}
      {open && (
        <div className={`absolute z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-2 ${align === 'right' ? 'right-0' : 'left-0'}`}>
          <DayPicker
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate || new Date()}
            fromDate={fromDate}
            toDate={toDate}
            onSelect={(day) => {
              if (day) {
                const yyyy = day.getFullYear();
                const mm = String(day.getMonth() + 1).padStart(2, '0');
                const dd = String(day.getDate()).padStart(2, '0');
                onSelect(`${yyyy}-${mm}-${dd}`);
                setOpen(false);
              }
            }}
            components={dayPickerComponents}
            classNames={dayPickerClassNames}
          />
        </div>
      )}
    </div>
  );
};

export default DatePickerDropdown;
export { dayPickerClassNames, dayPickerComponents };
